import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderDetailsCard } from '@components/OrderProcessing/OrderDetailsCard';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  CustomerModules,
  errorMessage,
  InvocieCrumbSteps,
  OrderProcessingMasterData,
  PickupCrumbSteps,
  timers,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { AllInvoiceProps } from '@helpers/Models';
import { formatDate } from '@services/OrderCreationService';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDismissDropdown } from '@services/DropdownService';

export const AllInvoices: React.FC = () => {
  const { state } = useLocation();
  const { orderId, fromPickup } = state || {};
  const [allInvoicesData, setAllInvoicesData] = useState<AllInvoiceProps[]>([]);
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const { showConfirmationToast } = UseConfirmationToast();

  // Close dropdown on outside click
  useDismissDropdown({
    refs: Array.from(dropdownRefs.current.values()).map((el) => ({
      current: el,
    })),
    onClose: () => setActiveDropdown(null),
  });

  useEffect(() => {
    if (orderId) {
      fetchAllInvoices();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.INVOICE_LIST);
      }, timers.TOAST_AUTO_CLOSE);
    }
  }, [orderId]);

  const fetchAllInvoices = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_ALL_INVOICES, {
        id: state.orderId,
        ...(fromPickup
          ? { moduleNumber: CustomerModules.PACKAGING_ORDER }
          : {}),
      });
      setAllInvoicesData(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleViewInvoice = (invoiceId: number): void => {
    const Order = {
      orderID: state.orderId,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      roles: state.roles,
      createdBy: state.createdBy,
      createdDate: state.createdDate,
      invoiceId: invoiceId,
      fromPickup: fromPickup ? true : false,
      orderId: orderId,
    };
    navigate(FrontendRoutes.INVOICE_DETAILS, { state: Order });
  };

  /**
   * Handles marking an order as picked up for a specific invoice.
   * @param invoiceId - The ID of the invoice to mark as picked up.
   */
  const handleConfirmPickedUp = async (invoiceId: number): Promise<void> => {
    try {
      await Axios.post(ApiEndpoints.MARK_AS_PICKEDUP, {
        id: invoiceId, // now correctly uses the invoice ID
      });
      showToastMessage('The order is marked as picked up', 'success');
      // Optionally refresh the list or update status
      await fetchAllInvoices();
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const toggleDropdown = (invoiceId: number) => {
    setActiveDropdown(activeDropdown === invoiceId ? null : invoiceId);
  };

  const fromPickupBreadCrumbs = {
    steps: PickupCrumbSteps,
    activeItem: 'All Invoices',
    type: OrderProcessingMasterData.PICKUP,
    stateProp: { orderId: state.orderId },
    orderNumber: state.orderNumber,
  };

  const fromOrderedItemListBreadCrumbs = {
    steps: InvocieCrumbSteps,
    activeItem: 'All Invoices',
    type: OrderProcessingMasterData.INVOICE,
    stateProp: { orderId: state.orderId },
    orderNumber: state.orderNumber,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {fromPickup ? (
        <SideNav active={7} />
      ) : (
        <SideNav active={6} />
      )}
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        {fromPickup ? (
          <OrderListBreadcrumbs orderInfo={fromPickupBreadCrumbs} />
        ) : (
          <OrderListBreadcrumbs orderInfo={fromOrderedItemListBreadCrumbs} />
        )}
        <div className="p-3">
          <div className="flex items-center gap-2 text-gray-700">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="p-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              All Invoices
            </h2>
          </div>
          {state && (
            <OrderDetailsCard
              orderDetailsCardInfo={{
                orderNumber: state.orderNumber,
                createdByUser: state.createdBy,
                roles: state.roles,
                customerName: state.customerName,
                orderDate: state.createdDate,
                customerEmail: state.customerEmail,
                customerPhone: state.customerPhone,
              }}
            />
          )}
          <div className="border-b mt-3 rounded-lg">
            <form>
              <table className="w-full table-auto rounded-xl">
                <thead className="bg-gray-100">
                  <tr className="border-b">
                    <th className="px-4 py-2 text-start font-semibold align-middle">
                      Invoice No.
                    </th>
                    <th className="px-4 py-2 text-start font-semibold align-middle">
                      Invoice Name
                    </th>
                    <th className="px-4 py-2 text-start font-semibold align-middle">
                      Date
                    </th>
                    <th className="px-4 py-2 text-start font-semibold align-middle">
                      Order Status
                    </th>
                    <th className="px-4 py-2 text-center font-semibold align-middle">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allInvoicesData.length > 0 ? (
                    allInvoicesData.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="hover:cursor-pointer hover:bg-tertiary hover:border hover:border-primary hover:b-2"
                        onClick={() => handleViewInvoice(invoice.id)}
                      >
                        <td className="px-4 py-2 font-semibold align-middle">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-2 text-start align-middle">
                          {invoice.invoiceName}
                        </td>
                        <td className="px-4 py-2 text-start align-middle">
                          {formatDate(invoice.invoiceDate)}
                        </td>
                        <td className="px-4 py-2 text-start align-middle">
                          <OrderStatusBadge
                            type="invoiceStatus"
                            status={invoice.status}
                          />
                        </td>
                        <td className="px-4 py-2 text-center relative align-middle">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(invoice.id);
                            }}
                            className="p-2"
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>

                          {activeDropdown === invoice.id && fromPickup && (
                            <div
                              className="absolute right-0 z-10 mt-2 w-48 p-2 bg-white border border-gray-200 rounded-lg shadow-lg"
                              ref={(el) => {
                                if (el) {
                                  dropdownRefs.current.set(invoice.id, el);
                                } else {
                                  dropdownRefs.current.delete(invoice.id);
                                }
                              }}
                            >
                              <button
                                onClick={() => {
                                  showConfirmationToast(
                                    'This action is final and cannot be undone. Please confirm the customer has collected their order.',
                                    'Confirm Pickup',
                                    'info',
                                    () => handleConfirmPickedUp(invoice.id),
                                    'Confirm Payment'
                                  );
                                  setActiveDropdown(null);
                                }}
                                type="button"
                                className="block w-full text-sm px-2 py-2 text-left hover:bg-gray-100"
                              >
                                Mark as Picked up
                              </button>
                              <button
                                onClick={() => {
                                  handleViewInvoice(invoice.id);
                                  setActiveDropdown(null);
                                }}
                                type="button"
                                className="block w-full text-sm px-2 py-2 text-left hover:bg-gray-100"
                              >
                                View Invoice
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-2 text-center align-middle">
                        No data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};